import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Autocomplete,
  Dialog,
  DialogTitle,
  SelectChangeEvent,
  Stack,
  useMediaQuery
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import {
  mobileWidth,
  ProgressButton,
  formatDayjsMaybe,
  globalDateFormat,
  useToast,
  SquareIconButton
} from "@qubit/autoparts";
import dayjs from "dayjs";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { getUserClientId } from "~/api/usersTypes/auth0Profile";
import { useAppDispatch, useAppSelector } from "~/app/store";

import { useDebounceSearch } from "~/hooks/useDebounce";

import { useBarcodeScanner } from "~/lib/barCodeScan";

import {
  variantToDisplayName,
  getInventoryDateObj,
  inventoryDateLabel,
  isAutostoreView,
  isExpirationValid,
  searchProduct
} from "~/lib/helpers";
import {
  mixpanelTrack,
  mixpanelTrackModalDismiss,
  useMixpanelModalName
} from "~/lib/mixpanel-tracking";
import { QUANTITY_MAX_VALUE } from "~/lib/shared";

import {
  closeBin,
  closePort,
  setSelectedAutostoreBinId
} from "~/redux/actions/autostore";
import {
  clearSelectedInventoryId,
  clearSelectedVariant,
  getVariantByVariantId,
  putAway
} from "~/redux/actions/inventory";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import {
  useLazyGetBinsByAutostoreBinNumberQuery,
  useLazyGetBinsBySearchQuery
} from "~/redux/warehouse/bin.hooks";
import {
  ProductSearchProduct,
  ProductSearchVariant,
  SearchBinRecord,
  VariantFrontendDto
} from "~/types/api";

type InventoryAddDialogProps = {
  /** If the variant does not exist, it means that the search should be displayed and select the desired product to add */
  autostorePortId: number | null;
  open: boolean;
  variant?: VariantFrontendDto;
  binId?: Guid | null;
  isFetchingBin?: boolean;
  disableClosePortOnClose?: boolean;
  onClose: () => void;
  refreshCb: () => void;
  getEmptyBin?: () => Promise<void>;
};

type UomInfo = {
  value: string;
  label: string;
};

export function InventoryAddDialog(props: InventoryAddDialogProps) {
  const {
    autostorePortId,
    binId,
    variant,
    isFetchingBin,
    disableClosePortOnClose,
    open
  } = props;

  // Misc hooks
  const isMobile = useMediaQuery(mobileWidth);
  const { t } = useTranslation();
  const { errorToast } = useToast();
  const dispatch = useAppDispatch();
  const { search } = useLocation();
  const trackedModalName = useMixpanelModalName("Add Inventory");

  // RTK Query hooks
  const [getBinsByAutostoreBinNumber] =
    useLazyGetBinsByAutostoreBinNumberQuery();
  const [searchBinsQuery] = useLazyGetBinsBySearchQuery();

  // Redux state
  const selectedAutostoreGridId = useAppSelector(
    (state) => state.workstations.siteWorkstation?.autostoreGridId
  );
  const selectedAutostoreBinId = useAppSelector(
    (state) => state.autostore.selectedAutostoreBinId
  );
  const selectedInventoryId = useAppSelector(
    (state) => state.inventory.selectedInventoryId
  );
  const clientId = useAppSelector((state) =>
    getUserClientId(state.login.profile)
  );
  const selectedVariant = useAppSelector(
    (state) => state.inventory.selectedVariant
  );
  const usersFulfillmentCenter = useAppSelector(
    (state) => state.store.usersFulfillmentCenter
  );
  const currentEmptyBin = useAppSelector(
    (state) => state.autostore.currentEmptyBin
  );

  const thisWorkstationId = useAppSelector(
    (state) => state.workstations.siteWorkstation?.id
  );
  const {
    inv_addSetExpirationEnabled,
    inv_addUomSelectionForASBinsDisabled,
    inv_addUseUomFromFC,
    inv_inventoryDateLabel,
    inv_uomOptions,
    inv_inventoryDateRequired
  } = useAppSelector(selectServerConfig);

  // Local state
  const [productInputSearch, setProductInputSearch] = useState<string | null>(
    null
  );
  const [productInputValue, setProductInputValue] =
    useState<ProductResult | null>(null);
  const [quantity, setQuantity] = useState<number | null>(null);
  const [listOfUoms, setListOfUoms] = useState<UomInfo[]>([]);
  const [unitOfMeasure, setUOM] = useState<string | null>();
  const [options, setOptions] = useState<SearchBinRecord[] | []>([]);
  const [selectedBin, setSelectedBin] = useState<SearchBinRecord | null>(null);
  const [inventoryDate, setInventoryDate] = useState<dayjs.Dayjs | null>(null);
  const [binFieldValue, setBinFieldValue] = useState<string | null>(null);
  const [binIdState, setBinIdState] = useState(selectedAutostoreBinId);
  const [autocompleteOptions, updateAutocompleteOptions] = useState<
    ProductResult[]
  >([] as ProductResult[]);

  const debouncedInput = useDebounceSearch(binFieldValue);
  type ProductResult = {
    type: string;
    variantId: Guid;
    displayText: string;
  };

  const disabled =
    (!variant && !selectedVariant) ||
    !quantity ||
    !isExpirationValid(
      inv_inventoryDateRequired || selectedVariant?.isExpirationRequired,
      inventoryDate
    ) ||
    (!selectedBin && !selectedAutostoreBinId && !binIdState);

  const handleQuantityChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value ? Number(event.target.value) : null;
    if (newValue !== null && newValue > QUANTITY_MAX_VALUE) {
      return;
    }
    setQuantity(newValue);
  };

  const handleBinIdChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (!event.target.value && !!currentEmptyBin?.openBinResponse.binId) {
      setBinIdState(null);
    } else {
      setBinIdState(
        event.target.value
          ? Number(event.target.value)
          : currentEmptyBin && currentEmptyBin?.openBinResponse.binId
      );
    }
  };

  const handleUOMChange = (event: SelectChangeEvent<unknown>) => {
    if (typeof event?.target?.value === "string") {
      mixpanelTrack({
        trackedPageName: trackedModalName,
        type: "Select Option",
        label: "UOM",
        eventProperties: {
          "New Value": event.target.value
        }
      });
      setUOM(event.target.value);
    }
  };

  const binSearchResponse = useCallback(
    async (binSearchText: string) => {
      if (isAutostoreView(search) && selectedAutostoreGridId) {
        // if at autostore port, search for autostore bins by selected grid
        return await getBinsByAutostoreBinNumber({
          autostoreGridId: selectedAutostoreGridId,
          binNumber: binSearchText
        }).unwrap();
      }
      // else if not at port, search all bins
      return await searchBinsQuery({ queryString: binSearchText }).unwrap();
    },
    [
      selectedAutostoreGridId,
      search,
      getBinsByAutostoreBinNumber,
      searchBinsQuery
    ]
  );

  const searchBins = useCallback(
    async (input: string): Promise<void> => {
      if (input.length) {
        const binSearch = await binSearchResponse(input);

        if (binSearch !== null && binSearch && binSearch.length === 1) {
          setSelectedBin(binSearch[0]);
        }
        if (binSearch) setOptions(binSearch);
      }
    },
    [binSearchResponse]
  );

  const createProductSearchOptions = (args: {
    hits: ProductSearchProduct[];
    exactUpcMatchFilter?: string;
  }): {
    type: string;
    variantId: string;
    displayText: string;
  }[] => {
    const { hits, exactUpcMatchFilter } = args;
    return hits
      .filter((hit: ProductSearchProduct) => {
        if (
          exactUpcMatchFilter &&
          ![...(hit.alternateLookups || []), hit.upc].includes(
            exactUpcMatchFilter
          )
        )
          return false;
        return true;
      })
      .flatMap((hit) =>
        hit.variants.map((v: ProductSearchVariant) => ({
          type: "product",
          variantId: v.productVariantId,
          displayText: variantToDisplayName(v, hit.name)
        }))
      );
  };

  const autocompleteSearchProducts = useCallback(
    async (input: string | null): Promise<void> => {
      if (!input) {
        return;
      }

      if (input.length && clientId) {
        const hits = await searchProduct(input);

        const hitsAsAutocompleteRecords = createProductSearchOptions({
          hits
        });
        updateAutocompleteOptions([...hitsAsAutocompleteRecords]);
      }
    },
    [clientId]
  );

  const debouncedSearch = useDebounceSearch(productInputSearch);
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (open) {
      const searchFunction = async () =>
        await autocompleteSearchProducts(debouncedSearch);
      void searchFunction();
    }
  }, [debouncedSearch, autocompleteSearchProducts, open]);

  const handleAutocompleteSelectInventoryAdd = async (
    option: ProductResult | null
  ): Promise<void> => {
    if (!option?.variantId) return;

    mixpanelTrack({
      trackedPageName: trackedModalName,
      type: "Select Search Result",
      label: "Select Product",
      eventProperties: {
        "Selected Product": option.displayText,
        "Variant ID": option.variantId
      }
    });

    const variant = await dispatch(getVariantByVariantId(option.variantId));
    setProductInputValue(option);

    // Only used on Inventory V1 Add Inventory and Induction V1 Bin Not Empty,
    // so we can get away with a bit of a hack.
    // Ideally the backend would be returning "ea", "cd", etc., but
    // it's not worth it to make that change right now since this is legacy code.
    let uomResult = variant?.pickingUnits;
    switch (variant?.pickingUnits) {
      case "Each":
        uomResult = "ea";
        break;
      case "Caddy":
        uomResult = "cd";
        break;
    }

    const uom =
      uomResult && inv_uomOptions?.some((u: UomInfo) => u.label === uomResult)
        ? uomResult
        : inv_uomOptions?.[0]?.label;

    if (uom) setUOM(uom);
  };

  // if bin field is enabled - handle bin search on scan
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let barcodeScanProperties: Parameters<typeof useBarcodeScanner<any>>[0];
  if (variant) {
    barcodeScanProperties = {
      disabled: !open,
      findScanMatch: async (buffer: string) => {
        setBinFieldValue(null); // set the bin field value to null, so that the inputs version of bin search doesn't also run
        const binRecords = await binSearchResponse(buffer);

        return binRecords && binRecords.length === 1 ? binRecords[0] : null;
      },
      processScanMatch: (matchedBin: SearchBinRecord | null) => {
        if (matchedBin) {
          setOptions([matchedBin]);
          setSelectedBin(matchedBin);
        }
      }
    };
  } else {
    // else, handle product search on scan
    barcodeScanProperties = {
      findScanMatch: async (buffer: string) => {
        const hits = clientId ? await searchProduct(buffer) : [];

        return hits.length
          ? createProductSearchOptions({
              hits
            })
          : false;
      },
      processScanMatch: (match: ProductResult[]) => {
        if (match) {
          void handleAutocompleteSelectInventoryAdd(match[0]);
        }
      }
    };
  }

  useBarcodeScanner(barcodeScanProperties);

  const clearModalState = () => {
    setQuantity(null);
    setProductInputValue(null);
    setProductInputSearch(null);
    updateAutocompleteOptions([]);
    setInventoryDate(null);
    setBinFieldValue(null);
    setSelectedBin(null);
  };

  const closeModal = async () => {
    props.refreshCb();
    props.onClose();
    if (productInputValue) {
      dispatch(clearSelectedVariant());
    }
    clearModalState();
    if (
      selectedAutostoreGridId &&
      autostorePortId &&
      binIdState &&
      !disableClosePortOnClose
    ) {
      await dispatch(
        closeBin({
          binId: binIdState
        })
      );
      await dispatch(closePort());
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (open && !isAutostoreView(search))
      dispatch(setSelectedAutostoreBinId(null));
  }, [selectedAutostoreBinId, open, dispatch, search]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (open && currentEmptyBin?.openBinResponse.binId) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setBinFieldValue(String(currentEmptyBin?.openBinResponse.binId));
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setBinIdState(currentEmptyBin?.openBinResponse.binId);
    }
  }, [currentEmptyBin?.openBinResponse.binId, open]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (open && debouncedInput) {
      void searchBins(debouncedInput);
    }
  }, [debouncedInput, open, searchBins]);

  /* If not inv_addUseUomFromFC, then use hard-coded UoM data. Otherwise,
  if the response for the current fulfillment center contains UoM data,
  use it and allow the user to select UoM from the drop-down menu and force the user to select a date. */
  useEffect(() => {
    if (
      inv_addUseUomFromFC &&
      usersFulfillmentCenter &&
      usersFulfillmentCenter?.availableUoms
    ) {
      const data = usersFulfillmentCenter.availableUoms.map((value) => ({
        value,
        label: value
      }));

      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setListOfUoms([...data]);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setUOM(data?.[0]?.label);
    } else {
      if (inv_addSetExpirationEnabled)
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
        setInventoryDate(dayjs().utc().add(1, "year"));
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setListOfUoms([...inv_uomOptions]);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setUOM(inv_uomOptions?.[0]?.label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleAddInventory = async (): Promise<Promise<void> | null> => {
    let autostoreBin = null;
    if (binIdState) {
      const binSearch = await binSearchResponse(binIdState.toString());
      if (!selectedBin && binSearch) {
        autostoreBin = binSearch.find((b) => b.bin_id === binId);
      }
      if (binSearch && binSearch.length === 0) {
        errorToast(`Bin with bin id: ${binIdState} doesn't exist`);
      }
    }
    if (
      (selectedBin || autostoreBin) &&
      (variant || selectedVariant) &&
      quantity &&
      isExpirationValid(
        inv_inventoryDateRequired || selectedVariant?.isExpirationRequired,
        inventoryDate
      ) &&
      unitOfMeasure
    ) {
      return dispatch(
        putAway(
          {
            autostoreGridId: selectedAutostoreGridId || undefined,
            autostorePortId: autostorePortId || undefined,
            binId: autostoreBin?.bin_id ?? selectedBin?.bin_id ?? "",
            variantId: variant?.variantId ?? selectedVariant?.variantId ?? "",
            count: { value: quantity, units: unitOfMeasure },
            ...getInventoryDateObj(inv_inventoryDateLabel, inventoryDate),
            lotNumber: null as unknown as string
          },
          thisWorkstationId
        )
      ).then(async () => {
        await closeModal();
      });
    }
    return null;
  };

  const quantityTextField = (
    <TextField
      id="quantity"
      label={t("quantity")}
      type="number"
      variant="outlined"
      value={quantity || ""}
      onChange={handleQuantityChange}
      onBlur={() => {
        if (quantity) {
          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Input Change",
            label: "Manual Quantity Input"
          });
        }
      }}
      inputProps={{
        sx: { fontSize: "50px", padding: "35px 10px 35px 18px" }
      }}
      InputLabelProps={{
        shrink: true
      }}
    />
  );

  const quantityButtons = (
    <Stack direction="column" gap={2} justifyContent="center">
      <SquareIconButton
        aria-label="increase-button"
        onClick={(): void => {
          const newQty = quantity ? quantity + 1 : 1;
          if (newQty > QUANTITY_MAX_VALUE) return;
          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Button Click",
            label: "Increase Quantity",
            eventProperties: {
              "Previous Quantity": quantity,
              "New Quantity": newQty
            }
          });
          setQuantity(newQty);
        }}
        size="large"
        disabled={quantity !== null && quantity >= QUANTITY_MAX_VALUE}
      >
        <ExpandLessIcon
          style={{
            fontSize: 27
          }}
        />
      </SquareIconButton>
      <SquareIconButton
        aria-label="decrease-button"
        onClick={(): void => {
          const newQty = quantity ? quantity - 1 : 0;
          mixpanelTrack({
            trackedPageName: trackedModalName,
            type: "Button Click",
            label: "Decrease Quantity",
            eventProperties: {
              "Previous Quantity": quantity,
              "New Quantity": newQty
            }
          });
          setQuantity(newQty);
        }}
        size="large"
      >
        <ExpandMoreIcon
          style={{
            fontSize: 27
          }}
        />
      </SquareIconButton>
    </Stack>
  );

  const unitOfMeasureField = (
    <TextField
      id="unitOfMeasure"
      label="UOM"
      type="string"
      select
      fullWidth
      variant="outlined"
      value={unitOfMeasure || ""}
      sx={{ flex: "1 0 200px" }}
      InputLabelProps={{
        shrink: true
      }}
      inputProps={{
        sx: { fontSize: "50px", padding: "50px 10px 20px 18px" }
      }}
      SelectProps={{
        onChange: handleUOMChange
      }}
      // disable UOM selection for autostore bins when config is true
      disabled={
        !!selectedAutostoreBinId && inv_addUomSelectionForASBinsDisabled
      }
    >
      {listOfUoms.map((choiceObj, i, arr) => (
        <MenuItem
          key={`uom-option-${choiceObj.value}`}
          className={`uom-option-${i}`}
          value={choiceObj.value}
          sx={{
            padding: "10px",
            fontSize: "24px",
            borderBottomColor: "gray.light",
            borderBottom: i === arr.length - 1 ? `1px solid` : "none"
          }}
        >
          {choiceObj.label}
        </MenuItem>
      ))}
    </TextField>
  );

  const binIdField = (
    <TextField
      id="autostoreBinId"
      label={`${t("bin")} Id`}
      type="string"
      fullWidth
      variant="outlined"
      value={binIdState || ""}
      onChange={handleBinIdChange}
      InputLabelProps={{
        shrink: true
      }}
      disabled={!variant}
    />
  );

  const dateLabel = inventoryDateLabel(inv_inventoryDateLabel);
  const capitalizedDateLabelForTracking =
    dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1);

  const inventoryDatePicker = (
    <DesktopDatePicker
      slotProps={{
        textField: {
          InputLabelProps: {
            shrink: true
          },
          onBlur: (e) => {
            mixpanelTrack({
              trackedPageName: trackedModalName,
              type: "Input Change",
              label: `${capitalizedDateLabelForTracking} (Manual Input)`,
              eventProperties: {
                Date: e.target.value
              }
            });
          },
          label: t(inventoryDateLabel(inv_inventoryDateLabel)),
          variant: "outlined",
          fullWidth: true,
          error: !isExpirationValid(
            inv_inventoryDateRequired || selectedVariant?.isExpirationRequired,
            inventoryDate
          )
        }
      }}
      label={t(inventoryDateLabel(inv_inventoryDateLabel))}
      value={dayjs(formatDayjsMaybe(inventoryDate))}
      format={globalDateFormat}
      onChange={(newValue) => {
        setInventoryDate(newValue);
      }}
      onAccept={(date) => {
        mixpanelTrack({
          trackedPageName: trackedModalName,
          type: "Select Option",
          label: `${capitalizedDateLabelForTracking} (Calendar Select)`,
          eventProperties: {
            Date: date ? date.format(globalDateFormat) : null
          }
        });
      }}
      onOpen={() => {
        mixpanelTrack({
          trackedPageName: trackedModalName,
          type: "Button Click",
          label: `Open ${capitalizedDateLabelForTracking} Calendar`
        });
      }}
    />
  );

  const AddInventoryButton = (
    <ProgressButton
      id="add-button"
      buttonSize="xLarge"
      emphasis="high"
      responsive
      variant="contained"
      color="primary"
      fullWidth
      startIcon={<AddIcon style={{ fontSize: 22 }} />}
      disabled={disabled || (isAutostoreView(search) && isFetchingBin)}
      onClick={async (): Promise<void | null> => {
        mixpanelTrack({
          trackedPageName: trackedModalName,
          type: "Button Click",
          label: "Add"
        });
        return handleAddInventory();
      }}
    >
      {t("add")}
    </ProgressButton>
  );

  const BinNotEmpty = (
    <ProgressButton
      buttonSize="xLarge"
      emphasis="high"
      responsive
      variant="contained"
      color="primary"
      fullWidth
      disabled={!binIdState || isFetchingBin}
      onClick={async () => {
        mixpanelTrack({
          trackedPageName: trackedModalName,
          type: "Button Click",
          label: "Bin Not Empty"
        });
        if (props.getEmptyBin && isAutostoreView(search))
          await props.getEmptyBin();
        if (selectedInventoryId) dispatch(clearSelectedInventoryId());
        dispatch(clearSelectedVariant());
      }}
    >
      {t("bin not empty")}
    </ProgressButton>
  );

  return (
    <Dialog
      onClose={(_e, reason) => {
        mixpanelTrackModalDismiss(trackedModalName, reason);
        void closeModal();
      }}
      open={open}
      maxWidth="tablet"
    >
      <DialogTitle sx={{ position: "relative" }}>
        <Stack>
          {t("add inventory")}
          <IconButton
            onClick={() => {
              mixpanelTrack({
                trackedPageName: trackedModalName,
                type: "Button Click",
                label: "Close"
              });
              void closeModal();
            }}
            aria-label="close"
            sx={{ position: "absolute", right: 1 }}
          >
            <CloseIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Stack spacing={2} padding={4}>
        {(!variant || productInputValue) && (
          <Autocomplete
            options={autocompleteOptions}
            filterOptions={(ops) => ops}
            getOptionLabel={(option) => option.displayText}
            style={{ width: "100%" }}
            onChange={(_e, option) => {
              void handleAutocompleteSelectInventoryAdd(option);
            }}
            value={productInputValue}
            onInputChange={(_e, newInputValue) => {
              setProductInputSearch(newInputValue);
            }}
            onBlur={() => {
              if (productInputSearch && !productInputValue) {
                mixpanelTrack({
                  trackedPageName: trackedModalName,
                  type: "Search",
                  label: "Search Product",
                  eventProperties: {
                    "Search Text": productInputSearch
                  }
                });
              }
            }}
            inputValue={productInputSearch || ""}
            isOptionEqualToValue={(option, value): boolean =>
              option.variantId === value.variantId
            }
            clearOnBlur={false}
            popupIcon={null}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                type="text"
                placeholder={t("search products")}
              />
            )}
          />
        )}

        {!selectedAutostoreBinId && (
          <Autocomplete<SearchBinRecord>
            options={options}
            id="bin-search"
            filterOptions={(ops) => ops}
            getOptionLabel={(option: SearchBinRecord): string =>
              option.location
            }
            style={{
              background: "white",
              margin: "14px 3px"
            }}
            onChange={(
              _e: ChangeEvent<unknown>,
              option: SearchBinRecord | null
            ): void => {
              if (option) {
                mixpanelTrack({
                  trackedPageName: trackedModalName,
                  type: "Select Search Result",
                  label: "Select Bin",
                  eventProperties: {
                    "Selected Bin": option.location,
                    "Search Text": binFieldValue || ""
                  }
                });
              }
              setSelectedBin(option);
              setBinFieldValue(null);
            }}
            inputValue={selectedBin?.location || binFieldValue || ""}
            value={selectedBin}
            popupIcon={null}
            blurOnSelect
            onBlur={() => {
              if (binFieldValue && !selectedBin?.location) {
                mixpanelTrack({
                  trackedPageName: trackedModalName,
                  type: "Search",
                  label: "Search Bin",
                  eventProperties: {
                    "Selected Bin": "",
                    "Search Text": binFieldValue
                  }
                });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                onChange={(e): void => {
                  // remove zebra check scan digit ("*")
                  setBinFieldValue(e.target.value.replace("*", ""));
                }}
                placeholder="Search Bin"
                variant="outlined"
                fullWidth
              />
            )}
          />
        )}
        {selectedAutostoreBinId && binIdField}
        <Stack spacing={2} direction="row">
          <Stack gap={2} direction="row" sx={{ flex: "3 0 400px" }}>
            {quantityTextField}
            {!isMobile && quantityButtons}
          </Stack>
          {unitOfMeasureField}
        </Stack>

        {inventoryDatePicker}
      </Stack>
      <Stack spacing={2} padding={4} style={{ marginTop: isMobile ? 20 : 0 }}>
        {AddInventoryButton}
        {isAutostoreView(search) && BinNotEmpty}
      </Stack>
    </Dialog>
  );
}
